import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import SavedProducts from "../../../services/SavedProducts";
import { SAVED_PRODUCTS_MUTATED_EVENT_NAME } from "../../Global/GlobalConstants";
import { fetchRefinementsCached } from "../../../utils/cachedFetch";

/**
 * Custom hook for managing saved products list
 * Handles fetching, pagination, and clearing products
 */
export const useSavedProducts = (servletUrl) => {
  const isFirstRender = useRef(true);
  const [products, setProducts] = useState([]);
  const [numberToShow, setNumberToShow] = useState(10);

  const NUMBERTOLOADMORE = 10;
  const { removeAll, toString } = useMemo(() => SavedProducts(), []);

  // Fetches products from servlet
  const fetchProducts = useCallback(() => {
    const productIds = toString();

    if (productIds) {
      const productIdsEncoded = encodeURIComponent(productIds);
      const productsRequest = servletUrl + "?productCodes=" + productIdsEncoded + "&includeInactive=true&includeAllData=true";

      fetchRefinementsCached(productsRequest)
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          throw response;
        })
        .then((data) => {
          setProducts(data);
        })
        .catch((error) => {
          console.error(error);
        });
    } else {
      setProducts([]);
    }
  }, [servletUrl, toString]);

  // Initialize and listen for product changes
  useEffect(() => {
    if (isFirstRender.current) {
      fetchProducts();
      isFirstRender.current = false;
      window.addEventListener(SAVED_PRODUCTS_MUTATED_EVENT_NAME, fetchProducts);

      return () => {
        window.removeEventListener(SAVED_PRODUCTS_MUTATED_EVENT_NAME, fetchProducts);
      };
    }
  }, [fetchProducts]);

  // Load more products
  const loadMore = useCallback(() => {
    const loadMoreAmount = numberToShow + NUMBERTOLOADMORE;
    setNumberToShow(loadMoreAmount);
  }, [numberToShow, NUMBERTOLOADMORE]);

  // Clear all products
  const clearList = useCallback(() => {
    removeAll();
  }, [removeAll]);

  // Print list
  const printList = useCallback(() => {
    window.print();
  }, []);

  return {
    products,
    numberToShow,
    productCount: (products && products.length) || 0,
    loadMore,
    clearList,
    printList,
  };
};
